import { useEffect, useState, useCallback } from 'react'
import { Link, useSearchParams, useLocation } from 'react-router-dom'
import { useLazyQuery, gql, useReactiveVar } from '@apollo/client'
import clsx from 'clsx'
import { PlusCircleIcon } from '@heroicons/react/24/solid'

import {
  generateSafeLink,
  serializeFromQuery,
  checkCompletion,
  normalizeCompletion,
} from '../utils'
import { EditableComponent, DialogEditLearningLoungeItems } from '../components'
import { ReactComponent as CheckIcon } from '../assets/icons/check-icon.svg'
import { ROLE_MANAGER } from '../constants'
import {
  apolloAuthData,
  apolloEditStatusData,
  getEditStatus,
  setChangesEditStatus,
} from '../services/apollo'
import { GET_USER_COMPLETION } from '../services/gql'
import { setAuthData } from '../hooks/useAuth'

const GET_LEARNING_LOUNGE_PAGES = gql`
  query LearningLoungePages {
    learningLoungePages {
      edges {
        node {
          databaseId
          postId
          title
          overview
        }
      }
    }
  }
`
const GET_LEARNING_LOUNGE_ITEMS = gql`
  query LearningLoungeItems {
    learningLoungeItems(first: 500) {
      edges {
        node {
          databaseId
          order
          postId
          title
          required
          thumbnail
          duration
          detail {
            questions {
              id
              choices {
                id
              }
            }
          }
        }
      }
    }
  }
`

const LearningLounge = () => {
  const auth = useReactiveVar(apolloAuthData)
  const isManager = auth.user?.capabilities?.includes(ROLE_MANAGER)
  let location = useLocation()
  let [searchParams, setSearchParams] = useSearchParams()
  const editStatus = useReactiveVar(apolloEditStatusData)
  const isEditOn = isManager && !!searchParams.get('edit')
  const [getUserCompletion] = useLazyQuery(GET_USER_COMPLETION, {
    fetchPolicy: 'no-cache',
  })
  const [getLearningLoungePages] = useLazyQuery(GET_LEARNING_LOUNGE_PAGES, {
    fetchPolicy: 'no-cache',
  })
  const [getLearningLoungeItems] = useLazyQuery(GET_LEARNING_LOUNGE_ITEMS, {
    fetchPolicy: 'no-cache',
  })
  const [learningLoungePage, setLearningLoungePage] = useState(null)
  const [learningLoungeItems, setLearningLoungeItems] = useState([])
  const [updatedItems, setUpdatedItems] = useState(null)
  const [isDataLoading, setIsDataLoading] = useState(true)
  const items =
    isEditOn && Array.isArray(updatedItems)
      ? [...updatedItems].sort((a, b) => a.order - b.order)
      : [...learningLoungeItems].sort((a, b) => a.order - b.order)

  const loadData = useCallback(async () => {
    setIsDataLoading(true)

    Promise.all([
      getLearningLoungePages(),
      getLearningLoungeItems(),
      getUserCompletion({ variables: { id: auth?.user?.id } }),
    ])
      .then((responses) => {
        // Learning lounge page
        const learningLoungePages =
          responses[0]?.data?.learningLoungePages?.edges?.map(
            (edge) => edge.node
          ) ?? []
        const _learningLoungePage = learningLoungePages[0] ?? null
        setLearningLoungePage(_learningLoungePage)

        // Learning lounge items
        const learningLoungeItems =
          responses[1]?.data?.learningLoungeItems?.edges?.map(
            (edge) => edge.node
          ) ?? []
        setLearningLoungeItems(learningLoungeItems)

        // User's completion
        const completion = normalizeCompletion(
          responses[2]?.data?.user?.completion
        )
        setAuthData({
          ...apolloAuthData(),
          user: { ...apolloAuthData().user, completion },
        })
      })
      .catch((err) => {
        console.error('Unable to load data', err)
      })
      .finally(() => {
        setIsDataLoading(false)
      })
  }, [
    getLearningLoungePages,
    getLearningLoungeItems,
    getUserCompletion,
    auth?.user?.id,
  ])

  // Load data every time the edit button is toggled
  useEffect(() => {
    if (isManager) {
      loadData()
    }
  }, [isManager, isEditOn, loadData])

  // Reload data when publish is successful
  useEffect(() => {
    if (!isEditOn) {
      return
    }

    if (editStatus.published === true) {
      loadData()
    }
  }, [editStatus.published, isEditOn, loadData])

  /**
   * Reload agenda items whenever the page is visited
   */
  useEffect(() => {
    if (location.pathname === '/learninglounge') {
      loadData()
    }
  }, [location.pathname, loadData])

  const handleOpenEditor = (required = true) => {
    // Open up the editor dialog
    setSearchParams({
      ...serializeFromQuery(searchParams),
      editor: 'true',
      required: required.toString(),
    })
  }

  const handleEditorConfirm = (updatedRowData = []) => {
    let newChanges = []
    let addChanges = []
    let removeChanges = []
    let reorderChanges = []
    const updatedRows = updatedRowData.map((row, rowIndex) => {
      const { _title, _required, _thumbnail, _duration, ...rest } = row
      return {
        ...rest,
        order: rowIndex,
        title: _title,
        required: _required,
        thumbnail: _thumbnail,
        duration: _duration,
      }
    })

    // Create the new change set
    removeChanges = items.filter(
      (row) => !!!updatedRows.find((item) => row.postId === item.postId)
    )
    addChanges = updatedRows.filter(
      (row) => !!!items.find((item) => row.postId === item.postId)
    )
    reorderChanges = updatedRows.filter(
      (row) =>
        items.find((item) => row.postId === item.postId)?.order !== row.order
    )
    let updateChanges = []
    updatedRows
      .filter(
        (row) =>
          // `required` change
          items.find((item) => row.postId === item.postId)?.required !==
            row.required ||
          // `title` change
          items.find((item) => row.postId === item.postId)?.title !==
            row.title ||
          // `duration` change
          items.find((item) => row.postId === item.postId)?.duration !==
            row.duration ||
          // `thumbnail` change
          items.find((item) => row.postId === item.postId)?.thumbnail !==
            row.thumbnail
      )
      .forEach((row) => {
        const origItem = items.find((item) => item.postId === row.postId)

        if (origItem) {
          if (row.required !== origItem.required) {
            updateChanges.push({ ...row, __update: 'required' })
          }

          if (row.title !== origItem.title) {
            updateChanges.push({ ...row, __update: 'title' })
          }

          if (row.duration !== origItem.duration) {
            updateChanges.push({ ...row, __update: 'duration' })
          }

          if (row.thumbnail !== origItem.thumbnail) {
            updateChanges.push({ ...row, __update: 'thumbnail' })
          }
        }
      })

    console.log('-> addChanges', addChanges)
    console.log('-> removeChanges', removeChanges)
    console.log('-> reorderChanges', reorderChanges)
    console.log('-> updateChanges', updateChanges)

    // Create change set from addChanges
    for (let i = 0; i < addChanges.length; i++) {
      const { changed, ...rest } = addChanges[i]
      const newChange = {
        page: 'LEARNING_LOUNGE_PAGE',
        type: 'item',
        action: 'create',
        content: rest,
      }

      newChanges.push(newChange)
    }

    // Create change set from removeChanges
    for (let i = 0; i < removeChanges.length; i++) {
      const { databaseId, postId } = removeChanges[i]

      if (typeof databaseId === 'number') {
        const newChange = {
          page: 'LEARNING_LOUNGE_ITEM',
          type: 'item',
          action: 'remove',
          databaseId,
        }

        newChanges.push(newChange)
      } else if (databaseId === undefined) {
        // Chances are this item was removed from the `addChanges`
        // User added this item but before publishing it, they decided to
        // remove it.
        const updatedChanges = getEditStatus().changes.filter(
          (change) => change.content?.postId !== postId
        )

        setChangesEditStatus({
          ...getEditStatus(),
          changes: updatedChanges,
        })
      }
    }

    // Create change set from updateChanges
    // Possible changes are: title, required, thumbnail
    for (let i = 0; i < updateChanges.length; i++) {
      const {
        databaseId,
        postId,
        title,
        __update,
        required,
        thumbnail,
        duration,
      } = updateChanges[i]

      if (databaseId === undefined) {
        // This is an update on the unpublished (newly added) item
        const updatedChanges = getEditStatus().changes.map((change) => {
          if (change.content?.postId === postId) {
            let updatedChange = { ...change }

            if (__update === 'required') {
              updatedChange.required = required
            } else if (__update === 'title') {
              updatedChange.title = title
            } else if (__update === 'duration') {
              updatedChange.duration = duration
            } else if (__update === 'thumbnail') {
              updatedChange.thumbnail = thumbnail
            }

            return updatedChange
          } else {
            return change
          }
        })

        setChangesEditStatus({
          ...getEditStatus(),
          changes: updatedChanges,
        })
      } else {
        if (__update === 'required') {
          newChanges.push({
            page: 'LEARNING_LOUNGE_PAGE',
            type: 'itemRequired',
            action: 'update',
            databaseId: databaseId,
            content: required,
          })
        } else if (__update === 'title') {
          newChanges.push({
            page: 'LEARNING_LOUNGE_PAGE',
            type: 'itemTitle',
            action: 'update',
            databaseId: databaseId,
            content: title,
          })
        } else if (__update === 'duration') {
          newChanges.push({
            page: 'LEARNING_LOUNGE_PAGE',
            type: 'itemDuration',
            action: 'update',
            databaseId: databaseId,
            content: duration,
          })
        } else if (__update === 'thumbnail') {
          newChanges.push({
            page: 'LEARNING_LOUNGE_PAGE',
            type: 'itemThumbnail',
            action: 'update',
            databaseId: databaseId,
            content: thumbnail,
          })
        }
      }
    }

    // Create change set from reorderChanges
    for (let i = 0; i < reorderChanges.length; i++) {
      const { order, databaseId } = reorderChanges[i]

      if (typeof databaseId === 'number') {
        const newChange = {
          page: 'LEARNING_LOUNGE_PAGE',
          type: 'itemOrder',
          // For learning lounge item post, value for action is `update`
          action: 'update',
          databaseId: databaseId,
          content: order,
        }
        newChanges.push(newChange)
      }
    }

    if (newChanges.length > 0) {
      setChangesEditStatus({
        ...getEditStatus(),
        changes: [...getEditStatus().changes, ...newChanges],
      })
    }

    setUpdatedItems(updatedRows)

    // Close the dialog
    handleEditorCancel()
  }

  const handleEditorCancel = () => {
    const { editor, required, ...rest } = serializeFromQuery(searchParams)
    setSearchParams(rest)
  }

  return (
    <main className="min-h-[calc(100vh-200px)] overflow-hidden">
      <div className="container mb-[120px] max-w-screen-content pt-14 sm:px-0">
        <div className={clsx('relative', isEditOn ? 'pb-[20px]' : 'pb-[10px]')}>
          <div className="absolute -right-[265px] top-[15px] -z-[1] h-[395px] w-[403px] bg-none bg-contain bg-right-top bg-no-repeat opacity-20 sm:bg-learninglounge-background"></div>
          {isDataLoading ? (
            <LoadingSkeleton />
          ) : !learningLoungePage ? (
            <p className="text-center">No data</p>
          ) : (
            <>
              <DialogEditLearningLoungeItems
                open={searchParams.get('editor') === 'true'}
                rowData={[
                  ...items.map((item) => {
                    const { title, required, thumbnail, duration, ...rest } =
                      item
                    return {
                      ...rest,
                      _title: item.title,
                      _required: item.required,
                      _thumbnail: item.thumbnail,
                      _duration: item.duration,
                    }
                  }),
                ]}
                title="Learning Lounge Editor"
                yesText="Save"
                noText="Cancel"
                onConfirm={handleEditorConfirm}
                onCancel={handleEditorCancel}
              />
              {/** Title */}
              <h1 className="mb-10 text-center text-4xl font-bold text-skin-accent">
                {isEditOn ? (
                  <EditableComponent
                    editable={isEditOn}
                    doc={learningLoungePage?.title ?? '<div></div>'}
                    toolbarOption={{
                      listNumber: false,
                      listBullet: false,
                    }}
                    tag={{
                      page: 'LEARNING_LOUNGE_PAGE',
                      type: 'title',
                      databaseId: learningLoungePage?.databaseId,
                      action: 'update',
                    }}
                    disableParagraph={true}
                  />
                ) : (
                  <div
                    dangerouslySetInnerHTML={{
                      __html: learningLoungePage?.title ?? '<div></div>',
                    }}
                  />
                )}
              </h1>
              {/** Overview */}
              {isEditOn ? (
                <EditableComponent
                  editable={isEditOn}
                  doc={learningLoungePage?.overview ?? '<div></div>'}
                  tag={{
                    page: 'LEARNING_LOUNGE_PAGE',
                    type: 'overview',
                    databaseId: learningLoungePage?.databaseId,
                    action: 'update',
                  }}
                />
              ) : (
                <div
                  dangerouslySetInnerHTML={{
                    __html: learningLoungePage?.overview ?? '<div></div>',
                  }}
                />
              )}
              {/** Legend */}
              <div className="my-20 flex justify-center">
                <div className="inline-block rounded-[14px] px-[44px] pb-[24px] pt-[16px] ring-2 ring-silver-sand">
                  <div className="mb-4 text-center leading-none">Legend</div>
                  <div className="flex justify-center gap-4 text-xs">
                    {['Required', 'Optional'].map((text) => (
                      <div
                        key={text}
                        className={`rounded-[5.5px] bg-bright-gray px-8 py-3 font-bold text-black-olive shadow-sm ${
                          text === 'Required' && 'ring-2 ring-beer'
                        }`}
                      >
                        {text}
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div
                className={clsx(
                  'group relative',
                  isEditOn &&
                    '-m-[15px] cursor-pointer p-[15px] outline outline-0 hover:outline-1 hover:outline-beer'
                )}
                onClick={isEditOn ? () => handleOpenEditor(true) : null}
              >
                {isEditOn && (
                  <div
                    className={clsx(
                      'absolute left-0 top-0 z-10 hidden h-full w-full bg-beer/25 group-hover:block'
                    )}
                  ></div>
                )}

                {/** Required list */}
                <div>
                  <h2 className="font-bold text-skin-accent">Required</h2>
                  {items.length === 0 ? (
                    isEditOn && (
                      <div className="flex items-center justify-center gap-x-1 text-sm">
                        <PlusCircleIcon className="h-[30px] w-[30px] fill-dark-charcoal/60 transition-all" />
                        <span className="text-dark-charcoal/60">
                          Click to add a first learning lounge item
                        </span>
                      </div>
                    )
                  ) : (
                    <ul
                      className={clsx(
                        'nostyle grid grid-cols-2 gap-4 sm:flex sm:grid-cols-none sm:flex-wrap md:gap-5'
                      )}
                    >
                      {items
                        .filter((item) => item.required)
                        .map((item) => {
                          const completed = checkCompletion(
                            auth?.user?.completion ?? [],
                            item.postId,
                            item.detail?.questions ?? []
                          )

                          return (
                            <li key={`${item.postId}${item.title}`}>
                              <div
                                className={clsx(
                                  'relative h-[155px] overflow-hidden rounded-[5.5px] bg-bright-gray bg-cover text-[12px] text-black-olive shadow-sm sm:w-[135px]',
                                  !isEditOn &&
                                    'transition-transform hover:scale-105',
                                  item.required && 'ring-2 ring-beer'
                                )}
                                style={{
                                  backgroundImage: item.thumbnail
                                    ? `url(${item.thumbnail})`
                                    : 'none',
                                }}
                              >
                                {/** Completion overlay */}
                                {completed && (
                                  <div className="absolute left-0 top-0 flex h-full w-full items-center justify-center bg-green/50"></div>
                                )}

                                {isEditOn ? (
                                  <div
                                    className={clsx(
                                      'flex h-full w-full flex-col justify-between px-2 py-1',
                                      item.thumbnail && 'backdrop-brightness-50'
                                    )}
                                  >
                                    <span
                                      className={clsx(
                                        item.thumbnail && item.thumbnail !== ''
                                          ? 'text-white'
                                          : 'text-dark-charcoal'
                                      )}
                                    >
                                      {item.duration ?? ''}
                                    </span>
                                    <div
                                      className={clsx(
                                        'flex items-end font-bold leading-snug',
                                        item.thumbnail && item.thumbnail !== ''
                                          ? 'text-white'
                                          : 'text-dark-charcoal'
                                      )}
                                      dangerouslySetInnerHTML={{
                                        __html: item.title ?? '<div></div>',
                                      }}
                                    />
                                  </div>
                                ) : (
                                  <Link
                                    to={generateSafeLink(item.postId)}
                                    className={clsx(
                                      'flex h-full w-full flex-col justify-between px-2 py-1',
                                      item.thumbnail &&
                                        'backdrop-brightness-50 backdrop-filter'
                                    )}
                                  >
                                    <div className="flex items-start justify-between">
                                      <span
                                        className={clsx(
                                          'leading-4',
                                          item.thumbnail &&
                                            item.thumbnail !== ''
                                            ? 'text-white'
                                            : 'text-dark-charcoal'
                                        )}
                                      >
                                        {item.duration ?? ''}
                                      </span>
                                      {/** Completion checkmark */}
                                      {completed && (
                                        <CheckIcon className="h-[14px] w-[14px] [&_path]:!fill-white" />
                                      )}
                                    </div>
                                    <div
                                      className={clsx(
                                        'flex items-end font-bold leading-snug',
                                        item.thumbnail && item.thumbnail !== ''
                                          ? 'text-white'
                                          : 'text-dark-charcoal'
                                      )}
                                      dangerouslySetInnerHTML={{
                                        __html: item.title ?? '<div></div>',
                                      }}
                                    />
                                  </Link>
                                )}
                              </div>
                            </li>
                          )
                        })}
                    </ul>
                  )}
                </div>

                {/** Optional list */}
                <div className="mt-14">
                  <h2 className="font-bold text-skin-accent">Optional</h2>
                  {items.length === 0 ? (
                    isEditOn && (
                      <div className="flex items-center justify-center gap-x-1 text-sm">
                        <PlusCircleIcon className="h-[30px] w-[30px] fill-dark-charcoal/60 transition-all" />
                        <span className="text-dark-charcoal/60">
                          Click to add a first learning lounge item
                        </span>
                      </div>
                    )
                  ) : (
                    <ul
                      className={clsx(
                        'nostyle grid grid-cols-2 gap-4 sm:flex sm:grid-cols-none sm:flex-wrap md:gap-5'
                      )}
                    >
                      {items
                        .filter((item) => !item.required)
                        .map((item) => {
                          const completed = checkCompletion(
                            auth?.user?.completion ?? [],
                            item.postId,
                            item.detail?.questions ?? []
                          )

                          return (
                            <li key={`${item.postId}${item.title}`}>
                              <div
                                className={clsx(
                                  'relative h-[155px] overflow-hidden rounded-[5.5px] bg-bright-gray bg-cover text-[12px] text-black-olive shadow-sm sm:w-[135px]',
                                  !isEditOn &&
                                    'transition-transform hover:scale-105',
                                  item.required && 'ring-2 ring-beer'
                                )}
                                style={{
                                  backgroundImage: item.thumbnail
                                    ? `url(${item.thumbnail})`
                                    : 'none',
                                }}
                              >
                                {/** Completion overlay */}
                                {completed && (
                                  <div className="absolute left-0 top-0 flex h-full w-full items-center justify-center bg-green/50"></div>
                                )}

                                {isEditOn ? (
                                  <div
                                    className={clsx(
                                      'flex h-full w-full flex-col justify-between px-2 py-1',
                                      item.thumbnail && 'backdrop-brightness-50'
                                    )}
                                  >
                                    <span
                                      className={clsx(
                                        item.thumbnail && item.thumbnail !== ''
                                          ? 'text-white'
                                          : 'text-dark-charcoal'
                                      )}
                                    >
                                      {item.duration ?? ''}
                                    </span>
                                    <div
                                      className={clsx(
                                        'flex items-end font-bold leading-snug',
                                        item.thumbnail && item.thumbnail !== ''
                                          ? 'text-white'
                                          : 'text-dark-charcoal'
                                      )}
                                      dangerouslySetInnerHTML={{
                                        __html: item.title ?? '<div></div>',
                                      }}
                                    />
                                  </div>
                                ) : (
                                  <Link
                                    to={generateSafeLink(item.postId)}
                                    className={clsx(
                                      'flex h-full w-full flex-col justify-between px-2 py-1',
                                      item.thumbnail &&
                                        'backdrop-brightness-50 backdrop-filter'
                                    )}
                                  >
                                    <div className="flex items-start justify-between">
                                      <span
                                        className={clsx(
                                          'leading-4',
                                          item.thumbnail &&
                                            item.thumbnail !== ''
                                            ? 'text-white'
                                            : 'text-dark-charcoal'
                                        )}
                                      >
                                        {item.duration ?? ''}
                                      </span>
                                      {/** Completion checkmark */}
                                      {completed && (
                                        <CheckIcon className="h-[14px] w-[14px] [&_path]:!fill-white" />
                                      )}
                                    </div>
                                    <div
                                      className={clsx(
                                        'flex items-end font-bold leading-snug',
                                        item.thumbnail && item.thumbnail !== ''
                                          ? 'text-white'
                                          : 'text-dark-charcoal'
                                      )}
                                      dangerouslySetInnerHTML={{
                                        __html: item.title ?? '<div></div>',
                                      }}
                                    />
                                  </Link>
                                )}
                              </div>
                            </li>
                          )
                        })}
                    </ul>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </main>
  )
}

const LoadingSkeleton = () => {
  return (
    <div role="status" className="animate-pulse space-y-2.5">
      {/** Title */}
      <div className="mb-10 flex w-full items-center justify-center space-x-2">
        <div className="mb-[28px] mt-[28px] h-2.5 w-2/5 rounded-full bg-gray-300 dark:bg-gray-700"></div>
      </div>
      {/** Overview */}
      <div className="flex w-full items-center space-x-2">
        <div className="h-2.5 w-full rounded-full bg-gray-200 dark:bg-gray-700"></div>
        <div className="h-2.5 w-full rounded-full bg-gray-300 dark:bg-gray-600"></div>
        <div className="h-2.5 w-24 rounded-full bg-gray-300 dark:bg-gray-600"></div>
      </div>
      <div className="flex w-full items-center space-x-2">
        <div className="h-2.5 w-full rounded-full bg-gray-200 dark:bg-gray-700"></div>
        <div className="h-2.5 w-24 rounded-full bg-gray-300 dark:bg-gray-600"></div>
        <div className="h-2.5 w-full rounded-full bg-gray-300 dark:bg-gray-600"></div>
      </div>
      <div className="flex w-full max-w-[400px] items-center space-x-2">
        <div className="h-2.5 w-full rounded-full bg-gray-300 dark:bg-gray-600"></div>
        <div className="h-2.5 w-80 rounded-full bg-gray-200 dark:bg-gray-700"></div>
        <div className="h-2.5 w-full rounded-full bg-gray-300 dark:bg-gray-600"></div>
      </div>
      {/** Legend */}
      <div className="flex justify-center">
        <div className="!mt-24 inline-block rounded-[14px] px-[44px] pb-[24px] pt-[16px] ring-2 ring-gray-300">
          <div className="!mb-4 flex w-full items-center justify-center">
            <div className="h-2.5 w-1/5 rounded-full bg-gray-300 dark:bg-gray-700"></div>
          </div>
          <div className="mx-auto flex w-full max-w-[300px] items-center gap-4">
            <div className="w-[125px] rounded-[5.5px] bg-gray-300 py-6 dark:bg-gray-600"></div>
            <div className="w-[127px] rounded-[5.5px] bg-gray-300 py-6 dark:bg-gray-600"></div>
          </div>
        </div>
      </div>
      {/** Learning lounge items */}
      <div className="mx-auto !mt-20 flex w-full items-center space-x-4">
        <div className="h-[155px] w-full rounded-[5.5px] bg-gray-300 dark:bg-gray-600"></div>
        <div className="h-[155px] w-full rounded-[5.5px] bg-gray-300 dark:bg-gray-600"></div>
        <div className="h-[155px] w-full rounded-[5.5px] bg-gray-300 dark:bg-gray-600"></div>
        <div className="h-[155px] w-full rounded-[5.5px] bg-gray-300 dark:bg-gray-600"></div>
      </div>
      <span className="sr-only">Loading...</span>
    </div>
  )
}

export default LearningLounge
